<template>
  <form :model="model" class="payment__form">
    <VInput
      v-for="(input, index) in fields"
      :key="index"
      v-bind="input"
      v-mask="getMaskType(input.name)"
      :label="input.placeholder"
      :type="input.type"
      :class="[getInputClass(input.name), { 'input--error': input.error }]"
      :errorMessage="input.error ? input.hint : ''"
      class="payment__input"
      :width="input.name === 'expirationDate' || input.name === 'securityCode' ? '48%' : '100%'"
      placeholder=""
      @focus="input.name === 'securityCode' ? setIsFlipped(true) : setIsFlipped(false)"
      @blur="input.name === 'securityCode' ? setIsFlipped(false) : null"
      @input="setValue($event, input)"
    />
    <h2>Em quantas vezes irá parcelar?</h2>
    <PaymentSelect :hint="select.hint" :items="select.options" @selected="selected" />
  </form>
</template>

<script>
import VInput from '@/components/commons/VInput';
import PaymentSelect from './form/PaymentSelect';
import validateCpf from '@/utils/helpers/validation/validateCpf';
import creditCardType from 'credit-card-type';
import { maskTypes } from '@/utils/helpers/maskers/maskTypes';
import { env } from '@/utils/enums/environment';

export default {
  name: 'PaymentForm',
  components: {
    PaymentSelect,
    VInput,
  },
  props: {
    error: Object,
    amount: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    fields: [
      {
        placeholder: 'Numero do cartão',
        type: 'tel',
        name: 'cardNumber',
        maxlength: 19,
        error: false,
        hint: 'Ops! Revise os dados do seu Cartão',
      },
      {
        placeholder: 'Nome do titular',
        type: 'text',
        name: 'holder',
        hint: 'Nome do titular inválido',
        error: false,
      },
      {
        placeholder: 'MM/AA',
        type: 'tel',
        name: 'expirationDate',
        maxlength: 5,
        hint: 'Data inválida',
        error: false,
      },
      {
        placeholder: 'Código (CVV)',
        type: 'tel',
        name: 'securityCode',
        maxlength: 5,
        hint: 'Código inválido',
        error: false,
      },
      {
        placeholder: 'CPF',
        type: 'tel',
        name: 'cpf',
        maxlength: 14,
        hint: 'Ops! Revise os dados do seu CPF',
        error: false,
      },
    ],
    formError: false,
    select: {
      name: 'installments',
      hint: undefined,
      options: [],
    },
    model: {
      installments: String,
      installmentsLabel: String,
      cardNumber: String,
      holder: String,
      expirationDate: String,
      securityCode: Number,
      brand: String,
      type: 'CreditCard',
      cpf: String,
    },
  }),
  methods: {
    showError(item) {
      item.error = true;
      this.updateFormError(true);
    },
    hideError(item) {
      item.error = false;
    },
    setDataModel(attribute, value) {
      this.$set(this.model, attribute, value);
    },
    selected(item) {
      this.model.installments = item.value;
      this.model.installmentsLabel = item.installmentsLabel;
      this.select.value = item.value;
      this.select.hint = '';
      this.checkForm();
    },
    setCpf(value, item) {
      item.value = value;
      const cpf = value.replace(/[.-]/g, '');
      this.setDataModel('cpf', cpf);

      if (cpf.length === 0) {
        this.hideError(item);
        return;
      }

      if (cpf.length >= 11) {
        if (validateCpf(cpf)) {
          this.hideError(item);
        } else {
          this.showError(item);
        }
      } else {
        this.showError(item);
      }
    },
    setCreditCard(value, item) {
      item.value = value;
      const number = value.replace(/\D/g, '');

      this.setDataModel('cardNumber', number);
      this.setBrand(number);

      if (!number || (number.length && number.length < 14)) {
        this.showError(item);
      } else {
        this.hideError(item);
      }
    },
    setBrand(number) {
      if (number && number.length >= 2) {
        this.model.brand = creditCardType(number)[0].type;
      } else {
        this.model.brand = undefined;
      }
    },
    isValidCreditCardNumer(number) {
      return !!creditCardType(number)[0];
    },
    setMaxLengthCVV() {
      if (this.model?.cardNumber && typeof this.model?.cardNumber === 'string') {
        const {
          code: { size },
        } = creditCardType(this.model.cardNumber)[0];
        return this.fields.forEach((field) => {
          if (field.name === 'securityCode') {
            field.maxlength = size;
          }
        });
      }
    },
    setHolder(value, item) {
      const regex = new RegExp('^[a-zA-Z ]+$');
      this.setDataModel('holder', value);
      if (!value || value.length < 5 || !regex.test(value)) {
        this.showError(item);
      } else {
        this.hideError(item);
      }

      item.value = value;
    },
    setExpirationDate(value, item) {
      const expiration = value.split('/');
      const year = new Date().getFullYear().toString().slice(-2);
      const month = new Date().getMonth() + 1;

      const itsMonthValid = year === expiration[1] ? expiration[0] >= month : true;

      const yearIsValidButMonthNo = expiration[1] === year && Number(expiration[0]) < month;
      const yearIsMinorThanActual = expiration[1] < year;
      const yearIsMajorOrEqualCurrent = Number(expiration[1]) >= year;
      const monthIsMajorThanValid = Number(expiration[0]) > 12;
      const monthIsValid = Number(expiration[0]) >= 1 && Number(expiration[0]) <= 12;

      this.setDataModel('expirationDate', `${expiration[0]}/20${expiration[1]}`);

      if (monthIsValid && yearIsMajorOrEqualCurrent && itsMonthValid) {
        this.hideError(item);
      } else if (
        value.length < 5 ||
        monthIsMajorThanValid ||
        yearIsMinorThanActual ||
        yearIsValidButMonthNo
      ) {
        this.showError(item);
      } else {
        this.hideError(item);
      }

      item.value = value;
    },
    setSecurityCode(value, item) {
      this.setMaxLengthCVV();

      const regex = /^\d*$/;

      this.setDataModel('securityCode', value);
      this.$emit('update:securityCode', true);

      if (value.length >= 3 && value.length <= item.maxlength && regex.test(value)) {
        this.hideError(item);
      } else if (!value || value.length < 3) {
        this.showError(item);
      } else {
        this.hideError(item);
      }

      item.value = value;
    },
    setValue(element, item) {
      const { name } = item;
      switch (name) {
        case 'cardNumber':
          this.setCreditCard(element, item);
          break;
        case 'holder':
          this.setHolder(element, item);
          break;
        case 'expirationDate':
          this.setExpirationDate(element, item);
          break;
        case 'securityCode':
          this.setSecurityCode(element, item);
          break;
        case 'cpf':
          this.setCpf(element, item);
          break;
      }
    },
    getMaskType(name) {
      return maskTypes[name];
    },
    getInputClass(index) {
      return `payment__input--${index}`;
    },
    getUndefinedFields() {
      return [...this.fields, this.select].filter((field) => field.value === undefined);
    },
    checkForm() {
      const listToCheck = this.getUndefinedFields();
      let updateFormError = false;

      if (listToCheck.length >= 1) {
        listToCheck.forEach((field) => this.showError(field));

        updateFormError = true;
      }

      this.updateFormError(updateFormError);
    },
    changeCurrency(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
    initializeInstallments() {
      for (let x = 1; x <= env.MAX_INSTALLMENTS_LENGTH; x++) {
        const installmentValue = this.changeCurrency(parseFloat(this.amount / x).toFixed(2));
        const installment = {
          label: `${x}x de ${installmentValue}`,
          installmentsLabel: `em ${x}x`,
          value: x,
        };
        this.select.options.push(installment);
      }
    },
    updateFormError(error) {
      this.formError = error;
      this.$emit('update:formError', error);
    },
    setIsFlipped(isFlipped) {
      this.$emit('setFlipped', isFlipped);
    },
  },
  created() {
    this.$emit('update:model', this.model);
    this.initializeInstallments();
  },
  beforeUpdate() {
    if (this.formError && this.getUndefinedFields().length === 0) {
      this.checkForm();
    }
  },
};
</script>

<style lang="scss">
.payment {
  &__form {
    margin-top: 10px;
    flex: 1;
    overflow: scroll;
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 8px;
    padding: 24px 8px 12px 8px;
    width: 100%;

    h2 {
      font-size: 16px;
      font-weight: 500;
      margin-top: 8px;
      text-align: left;
      width: 100%;
      margin-bottom: 16px;
    }
  }

  &__input {
    margin-bottom: 32px;
    border-bottom-width: 1px;

    &--cpf,
    &--cardNumber,
    &--holder {
      min-width: 100%;
    }

    &--expirationDate {
      margin-right: 6%;
    }

    &--expirationDate,
    &--securityCode {
      width: 47% !important;
    }

    &.input--error {
      color: var(--color-error);

      label {
        color: #ff3867 !important;
      }
      input {
        border-bottom-color: #ff3867 !important;
      }
    }

    input[value=''] + label {
      top: 34% !important;
    }

    input:not(:placeholder-shown) + label {
      top: 0.18rem !important;
    }

    input {
      min-width: 100%;
      padding: 18px 16px !important;
    }

    > div {
      position: absolute !important;
      bottom: -38% !important;
    }
  }

  .input__output {
    display: none;
  }

  &.input--error .input__output {
    background: transparent;
    color: var(--color-error);
    display: block;
    padding: 0;
    width: fit-content;
  }
}
</style>
